// @flow
import { takeEvery, call, put, select } from 'redux-saga/effects';
import type { Saga } from 'redux-saga';

import { getCustomizeLastQuestion } from 'services/api/sale';
import { getCampaign } from 'services/api/campaign';
import { getPurchasedVariants } from 'services/api/carts';
import { getUserAvatar } from 'services/api/account';
import {
  CAMPAIGN,
  receiveCampaign,
  receiveCampaignError,
} from 'services/actions/campaignActions';
import {
  setPrevPurchasedVariants,
  endLoadPrevCart,
} from 'services/actions/cartActions';
import {
  USER,
  fetchUserAvatarSuccess,
  fetchUserAvatarError,
} from 'services/actions/userActions';
import {
  requestCustomizeLastQuestion,
  receiveCustomizeLastQuestion,
  receiveCustomizeLastQuestionError,
} from 'services/actions/customizeActions';
import type { RequestAllDataAction } from 'services/actions/campaignActions';
import { getCampaignType, CAMPAIGN_TYPE } from 'utils/campaign';
import { fetchSaleData } from './sale';
import type { CampaignResponse, CartQuantities } from '../../sharedTypes';

const { REQUEST_ALL_DATA } = CAMPAIGN;
const SELECT = 'select';
const TWO_WINDOW = 'TWO_WINDOW';
const WINDOW_TWO = 'WINDOW_TWO';
const { FETCH_USER_AVATAR_REQUEST } = USER;

// worker sagas
export function* fetchCampaignData(): Saga<CampaignResponse | void> {
  try {
    const { loggedIn } = yield select((state) => state.userState);
    const campaignType = getCampaignType();
    const campaignData: CampaignResponse = yield call(
      getCampaign,
      campaignType,
      loggedIn
    );
    yield put(receiveCampaign(campaignData));
    return campaignData;
  } catch {
    yield put(receiveCampaignError());
  }
}

function* fetchBaseData(action: RequestAllDataAction) {
  const { userRole, flags } = action;
  const campaignData = yield call(fetchCampaignData);
  const { userAccess } = campaignData;
  const isSelectUser = userRole === SELECT;
  const campaignInSecondWindow =
    campaignData &&
    campaignData.windowType === TWO_WINDOW &&
    campaignData.campaignStatus === WINDOW_TWO;
  const campaignType = getCampaignType();
  const isShopPage = campaignType === CAMPAIGN_TYPE.shop;

  // get favorites, cart, categories and search terms if campaign is accessible to user
  if (userAccess || isShopPage) yield call(fetchSaleData, flags);

  /* if user is a select member and we are in the second window of a 2 window campaign,
      get the user's previous cart. This is so we can calculate product add-to-cart limits */
  if (isSelectUser && campaignInSecondWindow) {
    // using try catch here so that if this worker SAGA fails, it ends the loading state.
    try {
      const { id, type } = campaignData;
      const purchasedVariants: {|
        variantQuantities: CartQuantities,
      |} = yield call(getPurchasedVariants, id, type);
      const { variantQuantities } = purchasedVariants;
      yield put(setPrevPurchasedVariants(variantQuantities));
    } catch {
      yield put(endLoadPrevCart());
    }
  }
}

export function* fetchUserAvatar() {
  try {
    const data = yield call(getUserAvatar);
    yield put(fetchUserAvatarSuccess(data && data.userAvatarUrl));
  } catch (error) {
    yield put(fetchUserAvatarError(error));
  }
}
export function* fetchCustomizeLastQuestion() {
  try {
    yield put(requestCustomizeLastQuestion());
    const data = yield call(getCustomizeLastQuestion);
    yield put(receiveCustomizeLastQuestion(data.id, data.experimentInfo));
  } catch (error) {
    yield put(receiveCustomizeLastQuestionError(error));
  }
}

// watcher sagas
export function* watchFetchBaseData(): Saga<void> {
  yield takeEvery(REQUEST_ALL_DATA, fetchBaseData);
  yield takeEvery(FETCH_USER_AVATAR_REQUEST, fetchUserAvatar);
}
